/**
 * @fileoverview Input/output schemas for world-element-detailer-v3.
 *
 * This agent takes detected world elements and enriches them with
 * descriptions scaled by classification (critical/major/minor).
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  DetectedWorldElementSchema,
  ElementClassificationEnum,
  WorldElementSubtypeEnum,
} from '@/domains/world-building/schemas/world-element-detection';
import { WorldElementTypeV3Enum } from '@/domains/world-building/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Receives the merged/deduplicated detection list plus the source text.
 */
export const InputSchema = BaseFlowInputSchema.extend({
  textInput: z.string().describe('The source text for reference when writing descriptions.'),
  detectedElements: z.array(DetectedWorldElementSchema).describe(
    'The merged, deduplicated list of detected elements to enrich.'
  ),
}).merge(AGENT_SCHEMA_OBJECTS.loreContext.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * An enriched world element with an initialDescription added.
 */
const EnrichedWorldElementSchema = z.object({
  name: z.string().describe('The element name (must match a detected element).'),
  type: WorldElementTypeV3Enum.describe('The world element type.'),
  element: WorldElementSubtypeEnum.describe('The element sub-type.'),
  classification: ElementClassificationEnum,
  initialDescription: z.string().describe(
    'Description of the element based on the text. ' +
    'Length varies by classification: critical = 3-5 sentences, ' +
    'major = 2-3 sentences, minor = 1 sentence.'
  ),
});

export type EnrichedWorldElement = z.infer<typeof EnrichedWorldElementSchema>;

export const OutputSchema = z.object({
  elements: z.array(EnrichedWorldElementSchema).describe(
    'All elements with their enriched descriptions.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
